(function () {

  const SELECTORS = {
    anyTooltip: '[data-tooltip],[data-tooltip-url]',
    iconCandidates: 'svg[title], i[title], span[title], a[title], button[title], svg[aria-label], i[aria-label], span[aria-label], a[aria-label], button[aria-label]'
  };

  const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  const EDGE_PAD = 10;
  const CURSOR_PAD = 14; // distance from cursor when pointer-follow

  function closestTooltipRoot(el) {
    return el.closest('.tooltip');
  }

  function getInt(el, attr, fallback) {
    const v = parseInt(el.dataset[attr], 10);
    return Number.isFinite(v) ? v : fallback;
  }

  function getStr(el, attr, fallback) {
    const v = el.dataset[attr];
    return (typeof v === 'string' && v.length) ? v : fallback;
  }

  function ensureTooltipWrapper(el) {
    const existing = closestTooltipRoot(el);
    if (existing) return existing;

    const root = document.createElement('span');
    root.className = 'tooltip tooltip--theme-neutral';

    // defaults
    root.dataset.tooltipPlacement = el.dataset.tooltipPlacement || el.dataset.tooltipPosition || 'top';
    root.dataset.tooltipAnimation = el.dataset.tooltipAnimation || 'fade';
    root.dataset.tooltipDelayShow = el.dataset.tooltipDelayShow || el.dataset.tooltipDelay || '140';
    root.dataset.tooltipDelayHide = el.dataset.tooltipDelayHide || '80';
    root.dataset.tooltipHoverIntent = el.dataset.tooltipHoverIntent || '1';
    root.dataset.tooltipHoverIntentMs = el.dataset.tooltipHoverIntentMs || '160';
    root.dataset.tooltipPointerFollow = el.dataset.tooltipPointerFollow || '0';
    root.dataset.tooltipPointerFollowMode = el.dataset.tooltipPointerFollowMode || 'cursor';

    if (el.dataset.tooltipTheme) root.classList.add('tooltip--theme-' + el.dataset.tooltipTheme);
    if (el.dataset.tooltipBg) root.style.setProperty('--tooltip-bg', el.dataset.tooltipBg);
    if (el.dataset.tooltipColor) root.style.setProperty('--tooltip-color', el.dataset.tooltipColor);
    if (el.dataset.tooltipMaxw) root.style.setProperty('--tooltip-maxw', el.dataset.tooltipMaxw + 'px');

    el.parentNode.insertBefore(root, el);
    root.appendChild(el);

    el.classList.add('tooltip__trigger');
    if (!el.hasAttribute('tabindex') && el.tagName !== 'BUTTON' && el.tagName !== 'A' && el.tagName !== 'INPUT') {
      el.setAttribute('tabindex', '0');
    }

    const content = document.createElement('span');
    content.className = 'tooltip__content';
    content.setAttribute('role', 'tooltip');
    content.setAttribute('aria-hidden', 'true');
    content.dataset.ttAnim = root.dataset.tooltipAnimation || 'fade';
    content.innerHTML = `
      <span class="tooltip__inner">
        <span class="tooltip__text">Loading...</span>
        <button type="button" class="tooltip__close" aria-label="Close">×</button>
      </span>
      <span class="tooltip__arrow" aria-hidden="true"></span>
    `;
    root.appendChild(content);

    return root;
  }

  function setAnim(root) {
    const content = root.querySelector('.tooltip__content');
    const anim = getStr(root, 'tooltipAnimation', 'fade');
    content.dataset.ttAnim = anim;
  }

  function fillTooltipContent(root, triggerEl) {
    const content = root.querySelector('.tooltip__content');
    const textEl = content.querySelector('.tooltip__text');

    const url = triggerEl.dataset.tooltipUrl || root.dataset.tooltipUrl;
    const refresh = (triggerEl.dataset.tooltipRefresh === '1' || root.dataset.tooltipRefresh === '1');

    if (url) {
      if (root._ajaxLoaded && !refresh) return;
      textEl.textContent = 'Loading...';

      fetch(url, { credentials: 'same-origin' })
        .then(r => r.text())
        .then(html => {
          textEl.innerHTML = html;
          root._ajaxLoaded = true;
          requestAnimationFrame(() => smartPosition(root));
        })
        .catch(() => {
          const fallback = triggerEl.dataset.tooltip || root.dataset.tooltip || 'Tooltip';
          textEl.textContent = fallback;
          root._ajaxLoaded = true;
        });
      return;
    }

    const staticText = triggerEl.dataset.tooltip || root.dataset.tooltip || '';
    textEl.innerHTML = staticText;
  }

  function closeAll(exceptRoot) {
    document.querySelectorAll('.tooltip').forEach(root => {
      if (exceptRoot && root === exceptRoot) return;
      const content = root.querySelector('.tooltip__content');
      if (content && content.classList.contains('is-visible')) hideTooltip(root);
    });
  }

  // -----------------------
  // Pointer follow
  // -----------------------
  function pointerFollowEnabled(root) {
    return String(root.dataset.tooltipPointerFollow || '0') === '1';
  }

  function updatePointerFollow(root, ev) {
    if (!pointerFollowEnabled(root)) return;

    const content = root.querySelector('.tooltip__content');
    if (!content || !content.classList.contains('is-visible')) return;

    // We apply cursor-relative translation via CSS vars
    // This is additive on top of placement transforms.
    const mode = getStr(root, 'tooltipPointerFollowMode', 'cursor'); // cursor|anchor

    if (mode === 'cursor' && ev) {
      // Convert viewport cursor coords to root-local offset:
      const rootRect = root.getBoundingClientRect();
      const x = (ev.clientX - rootRect.left) + CURSOR_PAD;
      const y = (ev.clientY - rootRect.top) + CURSOR_PAD;

      root.style.setProperty('--tt-follow-x', `${x}px`);
      root.style.setProperty('--tt-follow-y', `${y}px`);
      // Note: for cursor mode we force an internal "top-start" feel visually,
      // but we keep placement for arrow/flip logic.
      return;
    }

    // anchor mode: no cursor tracking (keeps at anchor), but we allow minor smoothing
    root.style.setProperty('--tt-follow-x', `0px`);
    root.style.setProperty('--tt-follow-y', `0px`);
  }

  // -----------------------
  // Placement flip + nudge
  // -----------------------
  function basePlacement(p) {
    // return main side: top/bottom/left/right
    if (!p) return 'top';
    if (p.indexOf('top') === 0) return 'top';
    if (p.indexOf('bottom') === 0) return 'bottom';
    if (p.indexOf('left') === 0) return 'left';
    if (p.indexOf('right') === 0) return 'right';
    return 'top';
  }

  function alignOf(p) {
    // start/end/center
    if (!p) return 'center';
    if (p.indexOf('-start') > -1) return 'start';
    if (p.indexOf('-end') > -1) return 'end';
    return 'center';
  }

  function composePlacement(side, align) {
    if (!align || align === 'center') return side;
    return side + '-' + align;
  }

  function smartPosition(root) {
    const content = root.querySelector('.tooltip__content');
    if (!content || !content.classList.contains('is-visible')) return;

    // reset shift
    root.style.setProperty('--tt-shift-x', '0px');
    root.style.setProperty('--tt-shift-y', '0px');

    // Store preferred once
    if (!root._prefPlacement) root._prefPlacement = root.dataset.tooltipPlacement || 'top';

    const preferred = root.dataset.tooltipPlacement || 'top';
    const prefSide = basePlacement(preferred);
    const prefAlign = alignOf(preferred);

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // measure
    let rect = content.getBoundingClientRect();

    // Flip main side if overflow on that axis
    let side = prefSide;
    if (prefSide === 'top' && rect.top < EDGE_PAD) side = 'bottom';
    else if (prefSide === 'bottom' && rect.bottom > (vh - EDGE_PAD)) side = 'top';
    else if (prefSide === 'left' && rect.left < EDGE_PAD) side = 'right';
    else if (prefSide === 'right' && rect.right > (vw - EDGE_PAD)) side = 'left';

    const nextPlacement = composePlacement(side, prefAlign);
    if (nextPlacement !== root.dataset.tooltipPlacement) {
      root.dataset.tooltipPlacement = nextPlacement;
      // remeasure after style changes
      rect = content.getBoundingClientRect();
    }

    // Nudge into viewport
    let shiftX = 0, shiftY = 0;
    if (rect.left < EDGE_PAD) shiftX += (EDGE_PAD - rect.left);
    if (rect.right > (vw - EDGE_PAD)) shiftX -= (rect.right - (vw - EDGE_PAD));
    if (rect.top < EDGE_PAD) shiftY += (EDGE_PAD - rect.top);
    if (rect.bottom > (vh - EDGE_PAD)) shiftY -= (rect.bottom - (vh - EDGE_PAD));

    root.style.setProperty('--tt-shift-x', `${shiftX}px`);
    root.style.setProperty('--tt-shift-y', `${shiftY}px`);
  }

  // -----------------------
  // Hover intent + show/hide
  // -----------------------
  function showTooltip(root, triggerEl, ev) {
    clearTimeout(root._hideTimer);
    clearTimeout(root._showTimer);

    const delayShow = getInt(root, 'tooltipDelayShow', 140);
    const hoverIntent = String(root.dataset.tooltipHoverIntent || '1') === '1';
    const hoverIntentMs = getInt(root, 'tooltipHoverIntentMs', 160);

    const scheduleMs = hoverIntent ? Math.max(delayShow, hoverIntentMs) : delayShow;

    root._showTimer = setTimeout(() => {
      fillTooltipContent(root, triggerEl);
      setAnim(root);

      const content = root.querySelector('.tooltip__content');
      content.classList.add('is-visible');
      content.setAttribute('aria-hidden', 'false');

      // pointer follow init
      updatePointerFollow(root, ev);

      requestAnimationFrame(() => smartPosition(root));
    }, scheduleMs);
  }

  function hideTooltip(root) {
    clearTimeout(root._showTimer);
    clearTimeout(root._hideTimer);

    const delayHide = getInt(root, 'tooltipDelayHide', 80);

    root._hideTimer = setTimeout(() => {
      const content = root.querySelector('.tooltip__content');
      if (!content) return;
      content.classList.remove('is-visible');
      content.setAttribute('aria-hidden', 'true');

      root.style.removeProperty('--tt-shift-x');
      root.style.removeProperty('--tt-shift-y');
      root.style.removeProperty('--tt-follow-x');
      root.style.removeProperty('--tt-follow-y');

      // Restore preferred placement if we flipped
      if (root._prefPlacement) root.dataset.tooltipPlacement = root._prefPlacement;
    }, delayHide);
  }

  function toggleTooltip(root, triggerEl, ev) {
    const content = root.querySelector('.tooltip__content');
    const isOpen = content && content.classList.contains('is-visible');
    if (isOpen) hideTooltip(root);
    else showTooltip(root, triggerEl, ev);
  }

  function wireTooltip(root, triggerEl) {
    if (root._wired) return;
    root._wired = true;

    const content = root.querySelector('.tooltip__content');
    const closeBtn = root.querySelector('.tooltip__close');

    // desktop hover/focus
    if (!isTouch) {
      triggerEl.addEventListener('mouseenter', (e) => showTooltip(root, triggerEl, e));
      triggerEl.addEventListener('mouseleave', () => hideTooltip(root));
      triggerEl.addEventListener('focus', (e) => showTooltip(root, triggerEl, e));
      triggerEl.addEventListener('blur', () => hideTooltip(root));

      // pointer-follow tracking
      triggerEl.addEventListener('mousemove', (e) => updatePointerFollow(root, e));
      // also track on root (some triggers are SVG; pointer can leave)
      root.addEventListener('mousemove', (e) => updatePointerFollow(root, e));
    } else {
      // mobile tap to toggle + outside click closes
      triggerEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeAll(root);
        toggleTooltip(root, triggerEl, e);
      });
    }

    // close button
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        hideTooltip(root);
      });
    }

    // prevent inside click from closing (mobile)
    if (content) content.addEventListener('click', (e) => e.stopPropagation());

    // keep aligned on resize/scroll
    const onMove = () => smartPosition(root);
    window.addEventListener('resize', onMove, { passive: true });
    window.addEventListener('scroll', onMove, { passive: true });

    // ESC closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') hideTooltip(root);
    });
  }

  // -----------------------
  // Auto attach icons (title/aria-label)
  // -----------------------
  function autoAttachIcons() {
    document.querySelectorAll(SELECTORS.iconCandidates).forEach(el => {
      if (el.classList.contains('tooltip__trigger')) return;
      if (closestTooltipRoot(el)) return;

      const looksLikeIcon =
        el.tagName === 'SVG' ||
        el.classList.contains('icon') ||
        el.classList.contains('btn__icon') ||
        el.closest('.btn__icon') ||
        el.dataset.tooltipIconAuto === '1' ||
        el.classList.contains('js-tooltip-icon');

      if (!looksLikeIcon) return;

      const label = el.getAttribute('aria-label') || el.getAttribute('title') || '';
      if (!label) return;

      if (!el.dataset.tooltip) el.dataset.tooltip = label;
      if (el.hasAttribute('title')) el.removeAttribute('title');

      // nice defaults for icon tooltips
      if (!el.dataset.tooltipPlacement) el.dataset.tooltipPlacement = 'top';
      if (!el.dataset.tooltipAnimation) el.dataset.tooltipAnimation = 'fade';
      if (!el.dataset.tooltipDelayShow) el.dataset.tooltipDelayShow = '250';
      if (!el.dataset.tooltipHoverIntentMs) el.dataset.tooltipHoverIntentMs = '200';

      const root = ensureTooltipWrapper(el);
      wireTooltip(root, el);
    });
  }

  function initDataTooltips() {
    document.querySelectorAll(SELECTORS.anyTooltip).forEach(el => {
      if (closestTooltipRoot(el) && closestTooltipRoot(el)._wired) return;

      const root = ensureTooltipWrapper(el);

      // theme overrides on trigger
      if (el.dataset.tooltipTheme) root.classList.add('tooltip--theme-' + el.dataset.tooltipTheme);
      if (el.dataset.tooltipBg) root.style.setProperty('--tooltip-bg', el.dataset.tooltipBg);
      if (el.dataset.tooltipColor) root.style.setProperty('--tooltip-color', el.dataset.tooltipColor);

      // legacy support
      if (el.dataset.tooltipPosition && !el.dataset.tooltipPlacement) {
        el.dataset.tooltipPlacement = el.dataset.tooltipPosition;
      }

      wireTooltip(root, el);
    });
  }

  // Outside click closes
  document.addEventListener('click', () => closeAll());

  function initAll() {
    initDataTooltips();
    autoAttachIcons();
  }

  document.addEventListener('DOMContentLoaded', initAll);

  // Optional hooks if your theme dispatches custom events after partial loads:
  document.addEventListener('ajaxComplete', initAll);
  document.addEventListener('wp-block-rendered', initAll);

})();
